import { Injectable } from "@angular/core";
import { State, Action, StateContext, Selector } from "@ngxs/store";
import {
  JbpAction_comment,
  JbpAction_create,
  JbpAction_draft,
  JbpAction_export,
  JbpAction_download_pdf,
  JbpAction_getActions,
  JbpAction_getItems,
  JbpAction_getPriority,
  JbpAction_getStrategies,
  JbpAction_update,
} from "./jbp.actions";
import { Jbp } from "../models/jbp";
import { JbpService } from "../services/jbp.service";
import { JbpListDto, PaginationParamsDto } from "../models/jbp-list.dto";
import { tap } from "rxjs/operators";
import { IListResponseData } from "../models/list-reponse-data";
import { JbpPriority } from "../models/jbp-priority";
import { Observable, of } from "rxjs";
import { INgxsFormModel } from "../models/ngxs-form-model";
import * as moment from "moment";
import { JbpDto, JbpUpdateDto } from "../models/jbp.dto";
import { saveAs } from "file-saver";

export class JbpStateModel {
  calculatorTexts: string[][];
  addForm?: INgxsFormModel<{
    step1: {
      client: number;
    };
    step2: {
      barbers: number;
      billOfSale: string;
      markList: string[];
      otherMarkList: string;
      aboutClient: string;
    };
    step3: {
      priority: number;
      strategies: number;
      actionOnline: number;
      actionOffline: number;
      otherActions: string;
    };
    step4: {
      calculatorOne: string;
      calculatorTwo: string;
      calculatorThree: string;
    };
  }>;
  updateForm?: INgxsFormModel<JbpUpdateDto>;
  list: {
    data?: IListResponseData<Jbp[]>;
    filters?: JbpListDto;
    params: PaginationParamsDto;
  };
  priority: JbpPriority[];
  strategies: {
    [id: number]: JbpPriority[];
  };
  actions: {
    [id: number]: {
      online: JbpPriority[];
      offline: JbpPriority[];
    };
  };
}

@State<JbpStateModel>({
  name: "jbp",
  defaults: {
    calculatorTexts: [
      [
        "Aktualna l. nowych klientów tygodniowo na salon",
        "Średni paragon",
        "Oczekiwana dodatkowa l. nowych klientów tygodniowo na salon",
      ],
      [
        "Średnia l. klientów zapisanych z wizyty na wizytę w tygodniu",
        "Średni paragon",
        "Oczekiwana dodatkowa l. klientów z zapisem na kolejny termin tygodniowo na salon",
      ],
      [
        "Aktualna l. koloryzacji w tygodniu",
        "Średnia cena koloryzacji",
        "Oczekiwana dodatkowa l. koloryzacji w tygodniu",
      ],
      [
        "Aktualna l. sprzedawanych produktów w tygodniu",
        "Średnia cena produktu na półce",
        "Oczekiwana dodatkowa l. sprzedawanych produktów w tygodniu",
      ],
      [
        "Aktualna l. pielęgnacji w tygodniu",
        "Średnia cena pielęgnacji",
        "Oczekiwana dodatkowa l. pielęgnacji w tygodniu",
      ],
    ],
    list: {
      params: {
        direction: "desc",
        page: 1,
        perPage: 12,
        sort: "id",
      },
    },
    priority: [],
    strategies: {},
    actions: {},
  },
})
@Injectable()
export class JbpState {
  constructor(private jbpService: JbpService) {}

  @Selector()
  public static calculatorValue({ addForm }: JbpStateModel): any {
    const data1 =
      Number(addForm.model.step4.calculatorTwo) *
      Number(addForm.model.step4.calculatorThree) *
      12;
    const data2 =
      Number(addForm.model.step4.calculatorTwo) *
      Number(addForm.model.step4.calculatorThree) *
      moment().weeksInYear();
    return {
      data: {
        weeks_12: data1,
        year: data2,
      },
    };
  }

  @Selector()
  public static data({ list }: JbpStateModel): IListResponseData<Jbp[]> {
    return list.data;
  }

  @Selector()
  public static priority({ priority }: JbpStateModel): JbpPriority[] {
    return priority;
  }

  @Selector()
  public static calculatorTexts({
    calculatorTexts,
    addForm,
  }: JbpStateModel): string[] {
    const priorityId = addForm.model.step3.priority as any;
    return calculatorTexts[priorityId.id - 1];
  }

  @Selector()
  public static strategies({
    strategies,
    addForm,
  }: JbpStateModel): JbpPriority[] {
    const priorityId = addForm.model.step3.priority as any;
    return strategies[priorityId.id];
  }

  @Selector()
  public static actionsOffline({
    actions,
    addForm,
  }: JbpStateModel): JbpPriority[] {
    const strategyId = addForm.model.step3.strategies as any;
    return actions[strategyId.id].offline;
  }

  @Selector()
  public static actionsOnline({
    actions,
    addForm,
  }: JbpStateModel): JbpPriority[] {
    const strategyId = addForm.model.step3.strategies as any;
    return actions[strategyId.id].online;
  }

  @Action(JbpAction_getItems)
  getItems(
    { getState, patchState }: StateContext<JbpStateModel>,
    { data }: JbpAction_getItems
  ) {
    if (data.page) {
      patchState({
        list: {
          ...getState().list,
          params: {
            ...getState().list.params,
            page: data.page,
          },
        },
      });
    }
    if (data.filters) {
      patchState({
        list: {
          ...getState().list,
          filters: {
            ...getState().list.filters,
            ...data.filters,
          },
        },
      });
    }

    const params = getState().list.params;
    const filters = getState().list.filters;
    return this.jbpService
      .getItems({
        ...params,
        ...filters,
      })
      .pipe(
        tap((val) => {
          patchState({
            list: {
              data: val,
              params,
              filters,
            },
          });
        })
      );
  }

  @Action(JbpAction_getPriority)
  getPriority({ getState, patchState }: StateContext<JbpStateModel>) {
    const pioryty = getState().priority;
    if (pioryty.length > 0) {
      return of(pioryty);
    }
    return this.jbpService.getPriority().pipe(
      tap((val) => {
        patchState({
          priority: val,
        });
      })
    );
  }

  @Action(JbpAction_getStrategies)
  getStrategies(
    { getState, patchState }: StateContext<JbpStateModel>,
    { priorityId }: JbpAction_getStrategies
  ) {
    const strategies = getState().strategies;
    // if (strategies[priorityId] && strategies[priorityId].length > 0) {
    //   return of(strategies[priorityId]);
    // }
    return this.jbpService.getStrategies(priorityId).pipe(
      tap((val) => {
        console.log(val);
        patchState({
          strategies: {
            ...strategies,
            [priorityId]: val,
          },
        });
      })
    );
  }

  @Action(JbpAction_getActions)
  getActions(
    { getState, patchState }: StateContext<JbpStateModel>,
    { priorityId }: JbpAction_getActions
  ) {
    const actions = getState().actions;
    // if (actions[priorityId] && actions[priorityId]) {
    //   return of(actions[priorityId]);
    // }
    return this.jbpService.getActions(priorityId).pipe(
      tap((val) => {
        console.log(val);
        patchState({
          actions: {
            ...actions,
            [priorityId]: val,
          },
        });
      })
    );
  }

  @Action(JbpAction_create)
  create({ getState, patchState }: StateContext<JbpStateModel>) {
    const data: JbpDto = {
      ...getState().addForm.model.step2,
      ...getState().addForm.model.step4,
      priority: getState().addForm.model.step3.priority["id"],
      actionOffline: getState().addForm.model.step3.actionOffline["id"],
      actionOnline: getState().addForm.model.step3.actionOnline["id"],
      client: getState().addForm.model.step1.client["id"],
      otherActions: getState().addForm.model.step3.otherActions,
    };

    return this.jbpService.create(data);
  }

  @Action(JbpAction_update)
  update({ getState }: StateContext<JbpStateModel>, { id }: JbpAction_update) {
    console.log(getState());
    const data: JbpUpdateDto = {
      ...getState().updateForm.model,
    };
    return this.jbpService.update(data, id);
  }

  @Action(JbpAction_comment)
  comment(
    { getState, patchState }: StateContext<JbpStateModel>,
    { id, comment }: JbpAction_comment
  ) {
    return this.jbpService.comment(id, comment);
  }

  @Action(JbpAction_draft)
  draft(
    { getState, patchState }: StateContext<JbpStateModel>,
    { id, draft }: JbpAction_draft
  ) {
    return this.jbpService.draft(id, draft);
  }

  @Action(JbpAction_export)
  export(
    { getState, patchState }: StateContext<JbpStateModel>,
    { data }: JbpAction_export
  ) {
    return this.jbpService.export(data as any);
  }

  @Action(JbpAction_download_pdf)
  exportPdf(
    { getState, patchState }: StateContext<JbpStateModel>,
    { id, clientName }: JbpAction_download_pdf
  ): Observable<Blob> {
    return this.jbpService.downloadPdf(id, clientName);
  }

  // JbpAction_getStrategies
  // JbpAction_getActions

  // @Action(JbpAction_getItems)
  // getItems(
  //   { getState, setState }: StateContext<JbpStateModel>,
  //   { payload }: JbpAction
  // ) {
  //   const state = getState();
  //   setState({ items: [ ...state.items, payload ] });
  // }
}
